import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { api } from '@/App';
import { RefreshCw, Power, Play, Pause, Clock, Thermometer, Droplets, Wind, AlertTriangle, Battery } from 'lucide-react';

// Logos des marques
const LOGOS = {
  bosch: '🅱️',
  irobot: '🤖',
  gardena: '🌿',
};

// Composant carte appareil générique
const ApplianceCard = ({ 
  name, 
  brand, 
  type, 
  status = 'offline', 
  icon,
  details = [],
  program = null,
  progress = null,
  onToggle,
  online = false,
  isStandby = false,
}) => {
  const getStatusColor = () => {
    if (!online && !isStandby) return 'border-gray-600 bg-gray-900/30';
    if (isStandby) return 'border-yellow-600/50 bg-yellow-950/20';
    if (status === 'running') return 'border-green-500/50 bg-green-950/20';
    if (status === 'finished') return 'border-cyan-500/50 bg-cyan-950/20';
    if (status === 'error') return 'border-red-500/50 bg-red-950/20';
    return 'border-blue-500/30 bg-blue-950/20';
  };

  const getStatusText = () => {
    if (!online && !isStandby) return 'HORS LIGNE';
    if (isStandby) return 'EN ATTENTE';
    if (status === 'running') return 'EN COURS';
    if (status === 'finished') return 'TERMINÉ';
    if (status === 'error') return 'ERREUR';
    if (status === 'idle') return 'PRÊT';
    return status.toUpperCase();
  };

  const getStatusIcon = () => {
    if (!online && !isStandby) return <AlertTriangle className="w-4 h-4 text-gray-500" />;
    if (status === 'running') return <Play className="w-4 h-4 text-green-400" />;
    if (status === 'finished') return <Power className="w-4 h-4 text-cyan-400" />;
    if (status === 'error') return <AlertTriangle className="w-4 h-4 text-red-400" />;
    return <Power className="w-4 h-4 text-blue-400" />;
  };

  return (
    <div className={`p-4 rounded-xl border ${getStatusColor()} transition-all`}>
      {/* En-tête */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-2xl">{icon}</span>
          <div>
            <div className="font-mono font-bold text-white text-sm">{name}</div>
            <div className="text-[10px] text-gray-500">{brand}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {getStatusIcon()}
          <span className={`text-[10px] font-mono ${
            status === 'running' ? 'text-green-400' : 
            status === 'error' ? 'text-red-400' : 
            isStandby ? 'text-yellow-400' :
            online ? 'text-cyan-400' : 'text-gray-500'
          }`}>
            {getStatusText()}
          </span>
        </div>
      </div>

      {/* Programme en cours */}
      {program && online && (
        <div className="mb-3 p-2 rounded bg-black/40 border border-gray-800">
          <div className="text-[9px] text-gray-500 mb-1">PROGRAMME</div>
          <div className="text-sm font-mono text-cyan-400">{program}</div>
        </div>
      )}

      {/* Barre de progression */}
      {progress !== null && online && status === 'running' && (
        <div className="mb-3">
          <div className="flex justify-between text-[9px] text-gray-500 mb-1">
            <span>PROGRESSION</span>
            <span>{progress}%</span>
          </div>
          <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-cyan-500 to-green-500 transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}

      {/* Détails */}
      {details.length > 0 && online && (
        <div className="grid grid-cols-2 gap-2 mb-3">
          {details.map((detail, i) => (
            <div key={i} className="p-1.5 rounded bg-black/40 border border-gray-800 text-center">
              <div className="text-[8px] text-gray-500">{detail.label}</div>
              <div className="text-xs font-mono text-white">{detail.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Bouton contrôle */}
      {onToggle && online && (
        <button
          onClick={onToggle}
          className={`w-full py-2 rounded-lg font-mono text-sm font-bold transition-all ${
            status === 'running' 
              ? 'bg-orange-600 hover:bg-orange-500 text-white'
              : 'bg-green-600 hover:bg-green-500 text-white'
          }`}
        >
          {status === 'running' ? 'ARRÊTER' : 'DÉMARRER'}
        </button>
      )}

      {/* Message standby */}
      {isStandby && (
        <div className="mt-2 p-2 rounded bg-yellow-900/20 border border-yellow-700/30 text-center">
          <div className="text-[10px] font-mono text-yellow-400/70">INTÉGRATION EN ATTENTE</div>
          <div className="text-[8px] text-yellow-400/50">API non connectée</div>
        </div>
      )}

      {/* Message hors ligne */}
      {!online && !isStandby && (
        <div className="mt-2 p-2 rounded bg-gray-800/50 text-center">
          <div className="text-[10px] font-mono text-gray-500">APPAREIL NON CONNECTÉ</div>
        </div>
      )}
    </div>
  );
};

const AppliancesPage = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [homeConnectStatus, setHomeConnectStatus] = useState(null);
  const [homeConnectDevices, setHomeConnectDevices] = useState([]);
  const [ankerStatus, setAnkerStatus] = useState(null);
  const [ankerDevices, setAnkerDevices] = useState([]);
  const [error, setError] = useState(null);

  const loadData = async () => {
    try {
      setError(null);
      
      // Charger le statut Home Connect
      try {
        const response = await api.get('/integrations/homeconnect/status');
        if (response.data) {
          setHomeConnectStatus(response.data);
          if (response.data.appliances && response.data.appliances.length > 0) {
            setHomeConnectDevices(response.data.appliances);
          }
        }
      } catch (e) {
        console.log('Home Connect not available:', e.message);
        setHomeConnectStatus({ configured: false, authenticated: false });
      }
      
      // Charger le statut Anker Solix
      try {
        const [statusRes, devicesRes] = await Promise.all([
          api.get('/integrations/anker/status'),
          api.get('/integrations/anker/devices')
        ]);
        if (statusRes.data) setAnkerStatus(statusRes.data);
        if (devicesRes.data?.devices) setAnkerDevices(devicesRes.data.devices);
      } catch (e) {
        console.log('Anker not available:', e.message);
        setAnkerStatus(null);
      }
      
    } catch (e) {
      console.error('Error loading appliances:', e);
      setError(e.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 300000); // 5 minutes (économie API Tuya)
    return () => clearInterval(interval);
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    loadData();
  };

  // Icônes par type d'appareil
  const getApplianceIcon = (type) => {
    switch(type?.toLowerCase()) {
      case 'oven': return '🔥';
      case 'dishwasher': return '🍽️';
      case 'washer': return '👕';
      case 'dryer': return '🌀';
      case 'coffeemaker': return '☕';
      case 'robotvacuum': return '🤖';
      case 'lawnmower': return '🌿';
      default: return '📱';
    }
  };

  // Convertir le statut Home Connect
  const getStatus = (device) => {
    const opState = device.status?.OperationState?.toLowerCase() || '';
    if (opState.includes('run')) return 'running';
    if (opState.includes('finish')) return 'finished';
    if (opState.includes('error') || opState.includes('abort')) return 'error';
    if (opState.includes('ready') || opState.includes('inactive')) return 'idle';
    return 'idle';
  };

  // Extraire les détails
  const getDetails = (device) => {
    const details = [];
    const status = device.status || {};
    
    if (status.CurrentCavityTemperature) {
      details.push({ label: 'TEMP.', value: `${status.CurrentCavityTemperature}°C` });
    }
    if (status.TargetTemperature) {
      details.push({ label: 'CIBLE', value: `${status.TargetTemperature}°C` });
    }
    if (status.RemainingProgramTime) {
      const mins = Math.floor(status.RemainingProgramTime / 60);
      details.push({ label: 'RESTANT', value: `${mins} min` });
    }
    if (status.DoorState) {
      details.push({ label: 'PORTE', value: status.DoorState === 'Closed' ? 'FERMÉE' : 'OUVERTE' });
    }
    
    return details;
  };

  if (loading) {
    return (
      <Layout title="GESTION ÉLECTROMÉNAGER">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-cyan-400 font-mono animate-pulse">CHARGEMENT ÉLECTROMÉNAGER...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="GESTION ÉLECTROMÉNAGER">
      <div className="flex-1 flex flex-col min-w-0">
        {/* Titre */}
        <div className="flex items-center justify-between px-4 py-2 border-b border-gray-800">
          <div className="flex items-center gap-2">
            <span className="text-xl">🏠</span>
            <span className="font-mono font-bold text-cyan-400">GESTION ÉLECTROMÉNAGER</span>
          </div>
          <button 
            onClick={handleRefresh}
            className="p-2 rounded-lg bg-gray-800/50 hover:bg-gray-700/50 transition-all"
            disabled={refreshing}
          >
            <RefreshCw className={`w-4 h-4 text-cyan-400 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Contenu principal */}
        <div className="flex-1 p-4 overflow-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            
            {/* Section Bosch Home Connect */}
            <div className="lg:col-span-2">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-lg">{LOGOS.bosch}</span>
                <span className="font-mono font-bold text-blue-400 text-sm">BOSCH HOME CONNECT</span>
                {homeConnectStatus?.authenticated ? (
                  <span className="text-[10px] text-green-400 bg-green-900/30 px-2 py-0.5 rounded">CONNECTÉ</span>
                ) : (
                  <span className="text-[10px] text-yellow-400 bg-yellow-900/30 px-2 py-0.5 rounded">AUTH REQUISE</span>
                )}
              </div>
              
              {/* Barre d'état Home Connect avec boutons */}
              <div className="mb-3 p-2 rounded-lg bg-gray-900/50 border border-gray-700">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {homeConnectStatus?.authenticated ? (
                      <>
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                        <span className="text-[10px] text-green-400 font-mono">CONNECTÉ</span>
                      </>
                    ) : (
                      <>
                        <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                        <span className="text-[10px] text-yellow-400 font-mono">DÉCONNECTÉ</span>
                      </>
                    )}
                  </div>
                  
                  {/* Bouton Reconnecter - toujours visible */}
                  {homeConnectStatus?.auth_url && (
                    <a
                      href={homeConnectStatus.auth_url}
                      className="flex items-center gap-1.5 px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white text-[10px] font-mono rounded transition-all"
                    >
                      <RefreshCw className="w-3 h-3" />
                      {homeConnectStatus?.authenticated ? 'RE-AUTORISER' : 'CONNECTER'}
                    </a>
                  )}
                </div>
                
                {/* Message d'aide si non connecté */}
                {!homeConnectStatus?.authenticated && (
                  <div className="mt-2 text-[10px] text-yellow-400/70 font-mono">
                    Cliquez sur CONNECTER pour autoriser l'accès à vos appareils Bosch
                  </div>
                )}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Dynamically render Home Connect devices */}
                {homeConnectDevices.length > 0 ? (
                  homeConnectDevices.map((device, i) => {
                    const status = getStatus(device);
                    const details = getDetails(device);
                    const program = device.status?.SelectedProgram || null;
                    const progress = device.status?.Progress || null;
                    
                    return (
                      <ApplianceCard
                        key={device.haId || i}
                        name={device.name?.toUpperCase() || device.type?.toUpperCase() || 'APPAREIL'}
                        brand="Bosch Home Connect"
                        type={device.type}
                        icon={getApplianceIcon(device.type)}
                        status={status}
                        online={device.connected !== false}
                        program={program}
                        progress={progress}
                        details={details}
                      />
                    );
                  })
                ) : (
                  <div className="col-span-2 p-4 bg-gray-900/30 border border-gray-700 rounded-lg text-center">
                    <div className="text-gray-400 text-sm font-mono">Aucun appareil Home Connect</div>
                    <div className="text-gray-500 text-xs mt-1">Connectez votre compte dans Admin</div>
                  </div>
                )}
                
              </div>
            </div>

            {/* Section Robot Aspirateur - iRobot Roomba */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-lg">{LOGOS.irobot}</span>
                <span className="font-mono font-bold text-purple-400 text-sm">ASPIRATEUR ROBOT</span>
                <span className="text-[10px] text-yellow-400 bg-yellow-900/30 px-2 py-0.5 rounded">CONFIG EN COURS</span>
              </div>
              
              <ApplianceCard
                name="iROBOT ROOMBA"
                brand="iRobot"
                type="RobotVacuum"
                icon="🤖"
                status="idle"
                online={false}
                isStandby={true}
              />
              
              <div className="mt-3 p-3 rounded-lg bg-purple-900/20 border border-purple-500/30 text-xs font-mono">
                <div className="text-purple-400 font-bold mb-1">IP: 10.60.1.99</div>
                <div className="text-purple-400/70">• En attente des credentials BLID/Password</div>
                <div className="text-purple-400/70">• Commande: roomba-password 10.60.1.99</div>
              </div>
            </div>

            {/* Section Anker Solix - Batterie Solaire */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-lg">🔋</span>
                <span className="font-mono font-bold text-green-400 text-sm">BATTERIE SOLAIRE</span>
                {ankerStatus?.status === 'online' ? (
                  <span className="text-[10px] text-green-400 bg-green-900/30 px-2 py-0.5 rounded">CONNECTÉ</span>
                ) : ankerDevices.length > 0 ? (
                  <span className="text-[10px] text-yellow-400 bg-yellow-900/30 px-2 py-0.5 rounded">VEILLE</span>
                ) : (
                  <span className="text-[10px] text-gray-400 bg-gray-900/30 px-2 py-0.5 rounded">CONFIG</span>
                )}
              </div>
              
              {ankerDevices.length > 0 ? (
                ankerDevices.map((device, idx) => (
                  <div key={idx} className={`p-4 rounded-xl border ${
                    device.online ? 'border-green-500/50 bg-green-950/20' : 'border-cyan-500/30 bg-cyan-950/20'
                  } transition-all`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">🔋</span>
                        <div>
                          <div className="font-mono font-bold text-white text-sm">{device.device_name || 'SOLARBANK'}</div>
                          <div className="text-[10px] text-gray-500">Anker Solix • {device.model}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Power className={`w-4 h-4 ${device.online ? 'text-green-400' : 'text-cyan-400'}`} />
                        <span className={`text-[10px] font-mono ${device.online ? 'text-green-400' : 'text-cyan-400'}`}>
                          {device.online ? 'EN LIGNE' : 'VEILLE'}
                        </span>
                      </div>
                    </div>
                    
                    {/* Stats batterie */}
                    <div className="grid grid-cols-2 gap-2 text-[11px] font-mono">
                      <div className="p-2 rounded bg-black/30 border border-gray-800">
                        <div className="text-gray-500 text-[9px]">BATTERIE</div>
                        <div className="text-cyan-400 font-bold">
                          {ankerStatus?.battery_soc !== null ? `${ankerStatus.battery_soc}%` : '100%'}
                        </div>
                      </div>
                      <div className="p-2 rounded bg-black/30 border border-gray-800">
                        <div className="text-gray-500 text-[9px]">MODE</div>
                        <div className="text-yellow-400 font-bold">SECOURS</div>
                      </div>
                      {ankerStatus?.current_solar_power_w > 0 && (
                        <div className="p-2 rounded bg-black/30 border border-gray-800">
                          <div className="text-gray-500 text-[9px]">SOLAIRE</div>
                          <div className="text-green-400 font-bold">{ankerStatus.current_solar_power_w}W</div>
                        </div>
                      )}
                      {ankerStatus?.today_solar_kwh > 0 && (
                        <div className="p-2 rounded bg-black/30 border border-gray-800">
                          <div className="text-gray-500 text-[9px]">PROD. JOUR</div>
                          <div className="text-green-400 font-bold">{ankerStatus.today_solar_kwh} kWh</div>
                        </div>
                      )}
                    </div>
                    
                    <div className="mt-2 text-[9px] text-gray-500 font-mono text-center">
                      SN: {device.device_sn}
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-4 bg-gray-900/30 border border-gray-700 rounded-lg text-center">
                  <div className="text-gray-400 text-sm font-mono">Anker Solix non configuré</div>
                  <div className="text-gray-500 text-xs mt-1">Configurez dans Admin</div>
                </div>
              )}
            </div>

            {/* Section Tondeuses - En attente */}
            <div className="lg:col-span-3">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-lg">{LOGOS.gardena}</span>
                <span className="font-mono font-bold text-green-400 text-sm">TONDEUSES CONNECTÉES</span>
                <span className="text-[10px] text-yellow-400 bg-yellow-900/30 px-2 py-0.5 rounded">EN ATTENTE</span>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Tondeuse Bosch */}
                <ApplianceCard
                  name="TONDEUSE BOSCH INDEGO"
                  brand="Bosch"
                  type="Lawnmower"
                  icon="🌿"
                  status="standby"
                  online={false}
                  isStandby={true}
                />

                {/* Tondeuse Skil */}
                <ApplianceCard
                  name="TONDEUSE SKIL"
                  brand="Skil"
                  type="Lawnmower"
                  icon="🌿"
                  status="standby"
                  online={false}
                  isStandby={true}
                />
              </div>
            </div>

          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AppliancesPage;
